import { CloseOutlined } from '@ant-design/icons';
import { Button, Tooltip, Typography } from 'antd';
import { ScopeSectionRuleModeName, ScopeSectionRuleTypeName } from '@ydistri/api-sdk';
import React, { useCallback, useState } from 'react';
import { TbAdjustmentsMinus } from 'react-icons/tb';
import type { CustomTagProps } from 'rc-select/lib/BaseSelect';
import { ConfigurationFieldStatus } from '@ydistri/utils';
import ConfigurationMultiselect from '../../global/ConfigurationMultiselect/ConfigurationMultiselect';
import ConfigurationMultiselectTag from '../../global/ConfigurationMultiselect/ConfigurationMultiselectTag';
import MultiselectSelectedDataModal from '../../global/ConfigurationMultiselect/MultiselectSelectedDataModal';
import type { ScopeSectionRuleDraft } from './scopeEditorModel';
import { mergeScopeProductOptions } from './scopeProductOptions';
import ScopeProductMultiselect from './ScopeProductMultiselect';
import { ScopeRuleMultiselectEntity } from './scopeActiveEntityOptions';
import {
  ExclusionButton,
  ExclusionControl,
  ExclusionLabel,
  RuleLabel,
  RuleLabelHeader,
  RuleRow,
  RuleSelectionCount,
  RuleWarningIcon,
  ValueSelectControl,
} from './ScopeSectionRulesStyledComponents';

interface ScopeSectionRuleProps {
  getRuleMultiselectData: (
    type: ScopeSectionRuleTypeName,
    selectedValueIds?: number[],
  ) => ScopeRuleMultiselectEntity[];
  isLoading?: boolean;
  label: string;
  readOnly?: boolean;
  rule: ScopeSectionRuleDraft;
  warningMessages?: string[];
  onChange: (rule: ScopeSectionRuleDraft) => void;
  onRemove: () => void;
}

const ScopeSectionRule: React.FC<ScopeSectionRuleProps> = ({
  getRuleMultiselectData,
  isLoading = false,
  label,
  readOnly = false,
  rule,
  warningMessages = [],
  onChange,
  onRemove,
}) => {
  const [productOptions, setProductOptions] = useState<ScopeRuleMultiselectEntity[]>([]);
  const [isSelectedDataOpen, setIsSelectedDataOpen] = useState(false);
  const isProduct = rule.type === ScopeSectionRuleTypeName.Product;
  const multiselectData = isProduct
    ? productOptions
    : getRuleMultiselectData(rule.type, rule.valueIds);
  const isExcluded = rule.mode === ScopeSectionRuleModeName.Exclude;
  const hasWarning = warningMessages.length > 0;

  const toggleMode = useCallback(() => {
    onChange({
      ...rule,
      mode: isExcluded ? ScopeSectionRuleModeName.Include : ScopeSectionRuleModeName.Exclude,
    });
  }, [isExcluded, onChange, rule]);
  const resolveProductOptions = useCallback((resolvedOptions: ScopeRuleMultiselectEntity[]) => {
    setProductOptions(current => mergeScopeProductOptions(current, resolvedOptions));
  }, []);
  const renderScopeTag = (tagProps: CustomTagProps): React.ReactElement => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument -- Ant returns the numeric option values supplied below
    const option = multiselectData.find(item => item.id === Number(tagProps.value));

    return (
      <ConfigurationMultiselectTag
        {...tagProps}
        error={
          option?.isInactive ? `This ${label} is not active for the selected template.` : undefined
        }
      />
    );
  };

  return (
    <>
      <RuleRow
        $hasWarning={hasWarning}
        data-testid={`scope-rule-${rule.type}`}
        data-warning={hasWarning || undefined}
      >
        <td>
          <ExclusionControl>
            <Tooltip title={`${isExcluded ? 'Include' : 'Exclude'} ${label} in the Scope.`}>
              <ExclusionButton
                $isExcluded={isExcluded}
                aria-label={`${isExcluded ? 'Include' : 'Exclude'} ${label} rule`}
                danger
                icon={<TbAdjustmentsMinus />}
                type="default"
                disabled={readOnly}
                onClick={toggleMode}
              />
            </Tooltip>
            {isExcluded && <ExclusionLabel aria-hidden="true">Excluded</ExclusionLabel>}
          </ExclusionControl>
        </td>
        <td>
          <RuleLabel>
            <RuleLabelHeader>
              <Typography.Text strong>{label}</Typography.Text>
              {hasWarning && (
                <Tooltip
                  title={warningMessages.map(message => (
                    <div key={message}>{message}</div>
                  ))}
                >
                  <RuleWarningIcon aria-label={`${label} warning`} />
                </Tooltip>
              )}
            </RuleLabelHeader>
            <RuleSelectionCount
              aria-label={`Show selected ${label} values`}
              type="button"
              onClick={() => setIsSelectedDataOpen(true)}
            >
              {`${rule.valueIds.length} Selected`}
            </RuleSelectionCount>
          </RuleLabel>
        </td>
        <td>
          <ValueSelectControl>
            {isProduct ? (
              <ScopeProductMultiselect
                data={multiselectData}
                disabled={isLoading || readOnly}
                loading={isLoading}
                resolutionContextKey={rule.id}
                tagRender={renderScopeTag}
                value={rule.valueIds}
                onOptionsResolved={resolveProductOptions}
                onValueChange={valueIds => onChange({ ...rule, valueIds })}
              />
            ) : (
              <ConfigurationMultiselect
                data={multiselectData}
                disabled={isLoading || readOnly}
                loading={isLoading}
                options={multiselectData.map(item => ({
                  value: item.id,
                  label: item.label ?? `#${item.customerId} - ${item.title}`,
                  disabled: item.disabled,
                }))}
                placeholder={`Select ${label.toLowerCase()}`}
                popupMatchSelectWidth={false}
                showSelectionActions={false}
                size="normal"
                status={ConfigurationFieldStatus.DEFAULT}
                tagRender={renderScopeTag}
                value={rule.valueIds}
                width="100%"
                onValueChange={valueIds => onChange({ ...rule, valueIds })}
              />
            )}
          </ValueSelectControl>
        </td>
        <td>
          <Tooltip title={`Remove ${label} rule`}>
            <Button
              aria-label={`Remove ${label} rule`}
              danger
              icon={<CloseOutlined />}
              type="text"
              disabled={readOnly}
              onClick={onRemove}
            />
          </Tooltip>
        </td>
      </RuleRow>
      <MultiselectSelectedDataModal
        open={isSelectedDataOpen}
        selectedIds={rule.valueIds}
        data={multiselectData}
        onClose={() => setIsSelectedDataOpen(false)}
      />
    </>
  );
};

export default ScopeSectionRule;
